import {
  TransitionType,
  type LoaderResultLoaded,
  type NavigatorRouteRegistration,
  type PreloadableComponent,
} from '@github-ui/react-core/app-routing-types'
import {useCurrentRouteState} from '@github-ui/react-core/use-current-route-state'
import {SSRNetwork, type FeatureFlagMap, type ServerPreloadedSubscription} from '@github-ui/relay-environment'
import {Suspense} from 'react'
import type {Location, Params, PathParam} from '@github-ui/react-router'
import {
  type ConcreteRequest,
  type GraphQLResponse,
  RecordSource,
  type OperationType,
  type Variables,
} from 'relay-runtime'

import {
  EntryPointContainer,
  RelayEnvironmentProvider,
  loadEntryPoint,
  type EntryPoint,
  type EntryPointComponent,
  type PreloadedEntryPoint,
} from 'react-relay'

import type {GraphQLResult, GraphQLSubscriptionResult} from '@github-ui/fetch-graphql'
import {buildQueries} from './build-queries'
import type {RelayRouteProps, RouteParams, VariableTransformer} from './types'
import {CACHE_EVENTS, createCache, QUERY_CACHE_VERSION} from '@github-ui/relay-query-cache'
import {sendEvent} from '@github-ui/hydro-analytics'
import {IS_BROWSER} from '@github-ui/ssr-utils'
import {createVariablesKey, getDefaultValues} from '@github-ui/relay-query-cache/helpers'
import {getSwPreloadedQueries, clearSwPreloadedQueries} from '@github-ui/service-worker-connector/sw-preloaded-queries'

const defaultVariableTransformer: VariableTransformer<string> = variables => variables

type RelayPayloadType<TEntryPointComponent> = {
  entryPointReference: PreloadedEntryPoint<TEntryPointComponent>
}
type IslandPreloadedQuery = {
  queryId: string
  queryName: string
  variables: Variables
  result: GraphQLResult
  timestamp?: number
  injectedByServiceWorker?: boolean
}

type GraphQLResultWithMetadata = GraphQLResult & {
  injectedByServiceWorker?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RecordMap = Record<string, any>
type EmbeddedDataType = {
  payload: {
    preloadedQueries?: IslandPreloadedQuery[]
    preloaded_records?: RecordMap
    preloadedSubscriptions?: Record<string, Record<string, GraphQLSubscriptionResult>>
    best_match_sort_enabled?: boolean
    tabbed_timeline?: boolean
  }
  enabled_features?: FeatureFlagMap
}

type LoadSyncType = {
  pathParams: Params
  searchParams: URLSearchParams
  embeddedData?: EmbeddedDataType
}

type CoreLoaderArgs = {
  pathParams: Params
  location: Location
  embeddedData?: unknown
}

/**
 * A utility for building a route registration for a route given a defined Relay entry point).
 * @deprecated Relay is being deperecated in favor of DataRouter (https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router).
 * @experimental
 */
export function relayRoute<
  Path extends string,
  // The inferred queries passed to the EntryPointComponent as props
  PreloadedQueryProps extends Record<string, OperationType>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  PreloadedEntryPoints extends Record<string, EntryPoint<any, any> | undefined>,
  RuntimeProps extends NonNullable<unknown>,
  ExtraProps extends NonNullable<unknown>,
>(
  {
    path,
    resourceName,
    Component,
    queryConfigs,
    transformVariables = defaultVariableTransformer,
    title,
    relayEnvironment,
    fallback,
    maxAge,
    transitionType = TransitionType.FETCH_THEN_TRANSITION,
    cacheTTL,
    exclude_from_cache_key,
  }: RelayRouteProps<Path, PreloadedQueryProps, PreloadedEntryPoints, RuntimeProps, ExtraProps>,
  runtimeProps = {} as RuntimeProps,
): NavigatorRouteRegistration {
  type TEntryPointComponent = EntryPointComponent<PreloadedQueryProps, PreloadedEntryPoints, RuntimeProps, ExtraProps>

  const internalResourceName = resourceName || Component?.displayName
  if (internalResourceName === undefined) throw new Error('invalid internal resourceName')
  // construct a Relay entry point:
  const entryPoint: EntryPoint<TEntryPointComponent, RouteParams<PathParam<Path>>> = {
    root: {
      getModuleId: () => internalResourceName,
      getModuleIfRequired: () => Component,
      load: () => {
        if (Component.preload) {
          // eslint-disable-next-line github/no-then
          return Component.preload().then(module => module.default)
        }
        throw new Error('preload method is not defined on the component')
      },
    },
    getPreloadProps: rp => {
      return {
        queries: buildQueries(queryConfigs, rp, transformVariables, relayEnvironment, cacheTTL, exclude_from_cache_key),
      }
    },
  }

  // merge 2 maps of subscriptions, favoring the entries in the first map in case of key equality
  function mergeSubscriptions(
    a: Map<string, ServerPreloadedSubscription>,
    b: Map<string, ServerPreloadedSubscription> | undefined,
  ) {
    if (!b || b.size === 0) {
      return a
    }

    const mergedSubscriptions = new Map<string, ServerPreloadedSubscription>(a)

    for (const [key, value] of b) {
      if (!mergedSubscriptions.has(key)) {
        mergedSubscriptions.set(key, value)
      }
    }

    return mergedSubscriptions
  }

  const queryConfigLookup = new Map<string, {concreteRequest: ConcreteRequest; defaultValues: Variables | undefined}>()
  for (const queryConfigKey in queryConfigs) {
    const queryObject = queryConfigs[queryConfigKey as keyof typeof queryConfigs]
    const cr = queryObject?.concreteRequest
    if (cr && 'kind' in cr && cr.kind === 'Request') {
      const defaultValues = getDefaultValues(cr)
      queryConfigLookup.set(cr.fragment.name, {concreteRequest: cr, defaultValues})
    }
  }

  function addPreloadedQueryToMap(
    ssrPreloadedQueries: Map<string, Map<string, GraphQLResultWithMetadata>>,
    query: IslandPreloadedQuery,
  ): void {
    const {queryId, queryName, variables, result, timestamp, injectedByServiceWorker} = query
    let queryRecord = ssrPreloadedQueries.get(queryId)
    if (!queryRecord) {
      queryRecord = new Map<string, GraphQLResultWithMetadata>()
    }

    const config = queryConfigLookup.get(queryName)
    const key = createVariablesKey(variables, config?.defaultValues)

    if (!queryRecord.has(key)) {
      const extendedResult: GraphQLResultWithMetadata = {
        ...result,
        timestamp,
        ...(injectedByServiceWorker && {injectedByServiceWorker: true}),
      }
      queryRecord.set(key, extendedResult)
    }
    ssrPreloadedQueries.set(queryId, queryRecord)
  }

  function loadSync({
    pathParams,
    searchParams,
    embeddedData,
  }: LoadSyncType): LoaderResultLoaded<RelayPayloadType<TEntryPointComponent>> {
    // parse the preloaded queries from the embedded data use the embedded data
    const ssrPreloadedQueries = new Map<string, Map<string, GraphQLResultWithMetadata>>()
    if (embeddedData?.payload?.preloadedQueries) {
      for (const query of embeddedData.payload.preloadedQueries) {
        addPreloadedQueryToMap(ssrPreloadedQueries, query)
      }
    }

    // Read SW-cached preloaded queries via message channel (fetched in ReactBaseElement)
    if (IS_BROWSER) {
      const swQueries = getSwPreloadedQueries()
      if (swQueries) {
        for (const query of swQueries) {
          addPreloadedQueryToMap(ssrPreloadedQueries, {
            queryId: query.queryId,
            queryName: query.queryName,
            variables: query.variables,
            result: query.result as GraphQLResult,
            timestamp: query.timestamp,
            injectedByServiceWorker: query.injectedByServiceWorker,
          })
        }
        clearSwPreloadedQueries()
      }
    }
    const preloadedSubscriptions = new Map<string, ServerPreloadedSubscription>()
    if (embeddedData?.payload?.preloadedSubscriptions) {
      for (const [queryId, results] of Object.entries(embeddedData.payload.preloadedSubscriptions)) {
        const subscriptionResults = new Map<string, GraphQLSubscriptionResult>()
        for (const [args, result] of Object.entries(results)) {
          subscriptionResults.set(args, result)
        }
        preloadedSubscriptions.set(queryId, subscriptionResults)
      }
    }

    if (embeddedData?.payload?.preloaded_records) {
      relayEnvironment.getStore().publish(new RecordSource(embeddedData?.payload?.preloaded_records))
    }

    const {baseUrl, getPreloadedSubscriptions, stalePreloadedData, tabbedTimeline} = relayEnvironment.options as {
      baseUrl: string | undefined
      getPreloadedSubscriptions: () => Map<string, ServerPreloadedSubscription> | undefined
      stalePreloadedData?: boolean
      tabbedTimeline?: boolean
    }
    const loadedSubscriptions = getPreloadedSubscriptions ? getPreloadedSubscriptions() : undefined
    const queryCache = IS_BROWSER
      ? createCache<GraphQLResponse>({
          sendAnalyticsEvent: (eventType, target?, payload = {}) => {
            const context = {
              react: true,
              version: QUERY_CACHE_VERSION,
            }
            sendEvent(eventType, {...context, ...payload, target})
          },
          reportableEvents: CACHE_EVENTS.ANY,
        })
      : undefined

    const markPreloadedDataAsStale = (value: boolean) => {
      relayEnvironment.options = {
        ...(relayEnvironment.options as object),
        stalePreloadedData: value,
      }
    }

    const newNetwork = new SSRNetwork(
      ssrPreloadedQueries,
      embeddedData?.enabled_features,
      mergeSubscriptions(preloadedSubscriptions, loadedSubscriptions),
      baseUrl,
      maxAge,
      queryCache,
      stalePreloadedData,
      markPreloadedDataAsStale,
    )
    // @ts-expect-error `__setNet` is not exposed in types
    relayEnvironment.__setNet(newNetwork)

    relayEnvironment.options = {
      baseUrl,
      getPreloadedSubscriptions: () => newNetwork.getPreloadedSubscriptions(),
      stalePreloadedData: stalePreloadedData ?? false,
      // Pass best_match_sort_enabled from payload for SSR variable transformers
      // This enables correct sort computation during SSR when DOM is unavailable
      bestMatchSortEnabled: embeddedData?.payload?.best_match_sort_enabled,
      // During SPA navigation, embeddedData is undefined so tabbedTimeline won't be in the
      // payload. Read the previously-stored value from relay environment options to keep it available
      // for variable transformers across route transitions.
      tabbedTimeline: embeddedData?.payload?.tabbed_timeline ?? tabbedTimeline,
    }

    const entryPointReference = loadEntryPoint(
      {
        getEnvironment: () => {
          return relayEnvironment
        },
      },
      entryPoint,
      {pathParams, searchParams},
    )

    const isValid = () => {
      const anyQueriesDisposed = Object.values(entryPointReference.queries).some(query => query.isDisposed)
      return !anyQueriesDisposed
    }
    let actualTitle = title
    for (const param in pathParams) {
      actualTitle = actualTitle.replaceAll(`:${param}`, pathParams[param] ?? '')
    }
    return {
      // NOTE: it could make sense to replace this with a "loading" result if we add support for those — but if we do
      // that, we'll need to figure out how to determine that the entrypoint has loaded and update the state. Without
      // updating the state, the framework wouldn't know when to remove the loading bar, update the title, and set the
      // focus state.
      type: 'loaded',
      data: {entryPointReference},
      title: actualTitle,
      isValid,
    }
  }

  async function coreLoader({pathParams, location, embeddedData}: CoreLoaderArgs) {
    const loaderResult = loadSync({
      pathParams,
      searchParams: new URLSearchParams(location.search),
      embeddedData: embeddedData ? (embeddedData as EmbeddedDataType) : undefined,
    })

    if (transitionType === TransitionType.FETCH_THEN_TRANSITION) {
      // Wait for all queries within the entry point to resolve
      const promises = Object.values(loaderResult.data.entryPointReference.queries)
        .map(query => query.source?.toPromise())
        .filter(promise => promise !== undefined)

      if (promises.length > 0) {
        await Promise.allSettled(promises)
      }
    }

    return loaderResult
  }

  function loadFromEmbeddedData({
    pathParams,
    embeddedData,
    location,
  }: {
    pathParams: Params
    embeddedData: unknown
    location: Location
  }) {
    const castedEmbeddedData = embeddedData as EmbeddedDataType
    const {data, isValid} = loadSync({
      pathParams,
      embeddedData: castedEmbeddedData,
      searchParams: new URLSearchParams(location.search),
    })

    let actualTitle = title
    for (const param in pathParams) {
      actualTitle = actualTitle.replaceAll(`:${param}`, pathParams[param] ?? '')
    }
    return {data, title: actualTitle, isValid}
  }

  const Wrapper: PreloadableComponent = () => {
    const state = useCurrentRouteState<{type: 'loaded'; data: RelayPayloadType<TEntryPointComponent>}>()
    const entryPointReference = state?.data?.entryPointReference

    if (!entryPointReference) {
      return null
    }

    return (
      <RelayEnvironmentProvider environment={relayEnvironment}>
        <Suspense fallback={fallback}>
          <EntryPointContainer entryPointReference={entryPointReference} props={runtimeProps} />
        </Suspense>
      </RelayEnvironmentProvider>
    )
  }

  return {
    path,
    Component: Object.assign(Wrapper, {preload: Component.preload}),
    coreLoader,
    loadFromEmbeddedData,
    transitionType,
  }
}
